import { defineStore } from 'pinia'
import { ElNotification } from 'element-plus'
import router from '@/router'
import { listTopupOrders } from '@/api/topup-orders'
import { listWithdrawOrders } from '@/api/withdraw-orders'

/** 轮询间隔（毫秒） */
const POLL_MS = 20000
/** 有待审核时提示音间隔 */
const BEEP_INTERVAL_MS = 2800
const MUTE_KEY = 'finance_pending_notify_muted'

function readMuted() {
  try {
    return sessionStorage.getItem(MUTE_KEY) === '1'
  } catch (_) {
    return false
  }
}

function writeMuted(v) {
  try {
    if (v) sessionStorage.setItem(MUTE_KEY, '1')
    else sessionStorage.removeItem(MUTE_KEY)
  } catch (_) {
    /* ignore */
  }
}

export const useFinancePendingNotifyStore = defineStore('financePendingNotify', {
  state: () => ({
    topupPending: 0,
    withdrawPending: 0,
    /** 上一轮总数，null 表示尚未拉取过 */
    lastTotal: null,
    /** 登录后首次拉取且有待审，只弹一次总览 */
    sessionOverviewDone: false,
    pollTimer: null,
    beepTimer: null,
    muted: readMuted()
  }),

  getters: {
    pendingTotal: (s) => s.topupPending + s.withdrawPending,
    hasPending: (s) => s.topupPending > 0 || s.withdrawPending > 0
  },

  actions: {
    setMuted(v) {
      this.muted = !!v
      writeMuted(this.muted)
      if (this.muted) {
        this.stopBeepLoop()
      } else if (this.hasPending) {
        this.startBeepLoop()
      }
    },

    playBeep() {
      if (typeof window === 'undefined' || this.muted) return
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext
        if (!Ctx) return
        const ctx = new Ctx()
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'sine'
        osc.frequency.value = 720
        gain.gain.value = 0.07
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.start()
        osc.stop(ctx.currentTime + 0.12)
        setTimeout(() => {
          try {
            ctx.close()
          } catch (_) {}
        }, 250)
      } catch (_) {
        /* autoplay / AudioContext */
      }
    },

    startBeepLoop() {
      if (this.beepTimer || this.muted) return
      this.playBeep()
      this.beepTimer = window.setInterval(() => {
        if (!this.hasPending || this.muted) {
          this.stopBeepLoop()
          return
        }
        this.playBeep()
      }, BEEP_INTERVAL_MS)
    },

    stopBeepLoop() {
      if (this.beepTimer) {
        clearInterval(this.beepTimer)
        this.beepTimer = null
      }
    },

    syncSoundWithPending() {
      if (this.hasPending && !this.muted) {
        this.startBeepLoop()
      } else {
        this.stopBeepLoop()
      }
    },

    notifyPendingMessage(tp, wp, isIncrease) {
      const parts = []
      if (tp > 0) parts.push(`上分 ${tp} 笔`)
      if (wp > 0) parts.push(`下分 ${wp} 笔`)
      const body = `待审核：${parts.join('，')}`
      ElNotification({
        title: isIncrease ? '新的上下分待审核' : '有待审核的上下分订单',
        message: body,
        type: 'warning',
        duration: isIncrease ? 12000 : 8000,
        showClose: true,
        onClick: () => {
          if (tp >= wp && tp > 0) {
            router.push('/finance/topup-orders')
          } else if (wp > 0) {
            router.push('/finance/withdraw-orders')
          }
        }
      })
    },

    async pollOnce() {
      try {
        const [t, w] = await Promise.all([
          listTopupOrders({ page: 1, pageSize: 1, status: 'pending' }),
          listWithdrawOrders({ page: 1, pageSize: 1, status: 'pending' })
        ])
        const tp = Math.max(0, Number(t?.total ?? 0))
        const wp = Math.max(0, Number(w?.total ?? 0))
        const prev = this.lastTotal
        const cur = tp + wp

        this.topupPending = tp
        this.withdrawPending = wp

        if (prev === null) {
          this.lastTotal = cur
          if (cur > 0 && !this.sessionOverviewDone) {
            this.sessionOverviewDone = true
            this.notifyPendingMessage(tp, wp, false)
          }
          this.syncSoundWithPending()
          return
        }

        if (cur > prev) {
          this.notifyPendingMessage(tp, wp, true)
        }

        this.lastTotal = cur
        this.syncSoundWithPending()
      } catch (_) {
        /* 无权限或网络错误：静默 */
      }
    },

    startPolling() {
      if (this.pollTimer) return
      void this.pollOnce()
      this.pollTimer = window.setInterval(() => {
        void this.pollOnce()
      }, POLL_MS)
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
      this.stopBeepLoop()
      this.lastTotal = null
      this.sessionOverviewDone = false
      this.topupPending = 0
      this.withdrawPending = 0
    },

    /** 列表页审核完成后刷新待审数量，立即停止多余提示音 */
    async refreshAfterAction() {
      try {
        const [t, w] = await Promise.all([
          listTopupOrders({ page: 1, pageSize: 1, status: 'pending' }),
          listWithdrawOrders({ page: 1, pageSize: 1, status: 'pending' })
        ])
        const tp = Math.max(0, Number(t?.total ?? 0))
        const wp = Math.max(0, Number(w?.total ?? 0))
        this.topupPending = tp
        this.withdrawPending = wp
        this.lastTotal = tp + wp
        this.syncSoundWithPending()
      } catch (_) {
        /* ignore */
      }
    },

    bootstrapFromLogin() {
      this.muted = readMuted()
      this.startPolling()
    },

    teardown() {
      this.stopPolling()
    }
  }
})
